import {
  type ImportColumnDecision,
  type ImportItemType,
  type ImportMappingIssue,
  rerunSuggestions,
} from "@lafayette/import-mapping";
import type {
  StaffImportAliasList,
  StaffImportJobDetail,
  StaffImportPreview,
} from "@lafayette/schemas";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { itemTypeLabels } from "../../catalog/labels";
import { useReportDirty, useWithoutPrompt } from "../../catalog/unsaved";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { submitBlockers } from "../blockers";
import { columnsFromMapping, columnsFromPreview } from "../columns";
import { describeOutcome, type SaveMessage, useImportCommands } from "../commands";
import { CancelDraftDialog, DraftFacts } from "../components/parts";
import { Stepper } from "../components/Stepper";
import {
  type ColumnDecisions,
  describeCounts,
  isItemTypeMapped,
  sameMapping,
  toDecisionInputs,
  withoutDefaultItemTypeConflict,
} from "../decisions";
import { describeImportError } from "../errors";
import { isEditableJob } from "../labels";
import { useImportJob, useImportRequest, useImportRows } from "../queries";
import { reviewRowsFromPreview, reviewRowsFromRows } from "../review";
import { type ImportStepKey, isImportStepKey, nextStep } from "../stepper";
import { HeaderStep } from "../steps/HeaderStep";
import { type FocusRequest, MapStep } from "../steps/MapStep";
import { ReviewStep } from "../steps/ReviewStep";
import { SaveStep } from "../steps/SaveStep";
import { UploadStep } from "../steps/UploadStep";
import { type ReadFile, useUploadFlow } from "../steps/use-upload-flow";
import {
  emptyValidationSettings,
  settingsIssues,
  toSubmitSettings,
  type ValidationSettingsForm,
} from "../validation-settings";

type NavigationState = Readonly<{ step?: unknown; notice?: unknown }> | null;

/** The page of `/imports/new` and `/imports/:jobId`. A saved draft opens at the mapping step. */
export function ImportWizardPage() {
  const { jobId } = useParams();
  const job = useImportJob(jobId);

  if (jobId === undefined) return <ImportWizard key="new" detail={null} />;
  if (job.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the import" message="Reading the saved draft." />
    );
  }
  if (job.isError) {
    return (
      <AsyncState
        kind="error"
        title="The import could not be loaded"
        message={describeImportError(job.error).message}
        onRetry={() => void job.refetch()}
        accessory={<BackLink to="/imports">Back to imports</BackLink>}
      />
    );
  }
  if (job.data.mapping === null) {
    return (
      <AsyncState
        kind="error"
        title="This import has no mapping"
        message="There is no saved mapping to open. Start a new import."
        accessory={<BackLink to="/imports">Back to imports</BackLink>}
      />
    );
  }
  return <ImportWizard key={jobId} detail={job.data} />;
}

type Source = Readonly<{ preview: StaffImportPreview; objectPath: string; fileName: string }>;

function ImportWizard({
  detail: initialDetail,
}: Readonly<{ detail: StaffImportJobDetail | null }>) {
  // The mapping and review steps hold wide tables.
  useWideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const request = useImportRequest();
  const commands = useImportCommands();
  const withoutPrompt = useWithoutPrompt();

  const jobId = initialDetail?.job.id;
  // The cache holds the newest job: a command writes its answer there, a conflict reads it again.
  const live = useImportJob(jobId).data ?? initialDetail;
  const detail = live;
  const mapping = detail?.mapping ?? null;
  const readOnly = detail !== null && !isEditableJob(detail.job);

  const navState = location.state as NavigationState;
  const startStep: ImportStepKey = isImportStepKey(navState?.step)
    ? navState.step
    : detail === null
      ? "upload"
      : "map";
  const [step, setStep] = useState<ImportStepKey>(startStep);
  // The state of the move from "Save draft" is used once. The browser keeps history state across a
  // reload, so without this a reload would reopen the Save step with an old "The draft was saved."
  // notice. Cleared, a reload opens the saved draft at the mapping step.
  const hadNavigationState = location.state !== null;
  // Runs once, for the state this page was opened with.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (hadNavigationState) {
      withoutPrompt(() =>
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null }),
      );
    }
  }, []);

  const [source, setSource] = useState<Source | null>(null);
  const [decisions, setDecisions] = useState<ColumnDecisions>(mapping?.columnDecisions ?? []);
  const [defaultItemType, setDefaultItemType] = useState<ImportItemType | null>(
    mapping?.defaultItemType ?? null,
  );
  const [message, setMessage] = useState<SaveMessage | null>(
    navState?.notice === "saved" ? { tone: "success", text: "The draft was saved." } : null,
  );
  const [serverIssues, setServerIssues] = useState<readonly ImportMappingIssue[]>([]);
  const [attempted, setAttempted] = useState(false);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [settings, setSettings] = useState<ValidationSettingsForm>(emptyValidationSettings);
  const [settingsAttempted, setSettingsAttempted] = useState(false);
  const [headerBusy, setHeaderBusy] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const blockersRef = useRef<HTMLFieldSetElement>(null);
  const nonce = useRef(0);
  // After a cancel the button that opened the dialog is gone (the draft is read-only). The dialog cannot
  // give the focus back to it, so the heading of the step gets it and the focus is not lost to the page.
  useEffect(() => {
    if (!cancelling && readOnly && document.activeElement === document.body) {
      document.getElementById(`step-${step}-title`)?.focus();
    }
  }, [cancelling, readOnly, step]);
  const keepFocus = useRef(false);
  const firstStep = useRef(true);

  // A keyboard user who presses "Continue" would lose the focus: the button is gone with its step. The
  // heading of the new step gets it, so a screen reader reads where the user is. Not on first render,
  // and not when a column is about to be focused (the blocked submit).
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    if (keepFocus.current) {
      keepFocus.current = false;
      return;
    }
    document.getElementById(`step-${step}-title`)?.focus();
  }, [step]);

  const rowsQuery = useImportRows(jobId);
  const savedRows = useMemo(
    () => rowsQuery.data?.pages.flatMap((page) => page.rows) ?? [],
    [rowsQuery.data],
  );

  const preview = source?.preview ?? null;
  const columns = useMemo(
    () =>
      mapping !== null
        ? columnsFromMapping(mapping, savedRows)
        : preview !== null
          ? columnsFromPreview(preview)
          : [],
    [mapping, savedRows, preview],
  );
  const reviewRows = useMemo(
    () =>
      mapping !== null
        ? reviewRowsFromRows(savedRows)
        : preview !== null
          ? reviewRowsFromPreview(preview)
          : [],
    [mapping, savedRows, preview],
  );

  // A file that was read but not saved as a draft, or a mapping that differs from the saved one.
  const dirty =
    !readOnly &&
    (mapping === null
      ? source !== null
      : !sameMapping(
          { decisions, defaultItemType },
          { decisions: mapping.columnDecisions, defaultItemType: mapping.defaultItemType },
        ));
  useReportDirty("import", dirty);

  const hasSource = preview !== null || detail !== null;
  const context = { hasSource };

  // --- step 1: upload and read ------------------------------------------------------------------

  const onRead = useCallback((read: ReadFile) => {
    setSource(read);
    setDecisions(read.preview.suggestions);
    setDefaultItemType(null);
    setMessage(null);
    setServerIssues([]);
    setAttempted(false);
  }, []);
  const upload = useUploadFlow(onRead);

  function chooseAnotherFile() {
    upload.reset();
    setSource(null);
    setDecisions([]);
    setDefaultItemType(null);
    setMessage(null);
    setServerIssues([]);
    setAttempted(false);
  }

  // --- step 2: sheet and header row -------------------------------------------------------------

  async function changeHeader(change: Readonly<{ sheetIndex: number; headerRowNumber?: number }>) {
    if (source === null) return;
    setHeaderBusy(true);
    setHeaderError(null);
    try {
      const next = await request<StaffImportPreview>(
        { command: "previewFile" },
        { objectPath: source.objectPath, ...change },
      );
      setSource({ ...source, preview: next });
      // The columns changed, so the old decisions no longer apply.
      setDecisions(next.suggestions);
      setDefaultItemType((current) => withoutDefaultItemTypeConflict(next.suggestions, current));
      setServerIssues([]);
      setAttempted(false);
    } catch (error) {
      const failure = describeImportError(error);
      setHeaderError(
        failure.kind === "parse" && failure.parseErrors.length > 0
          ? "The server could not read this sheet or header row. Choose another."
          : failure.message,
      );
    } finally {
      setHeaderBusy(false);
    }
  }

  // --- step 3: mapping --------------------------------------------------------------------------

  async function rerun() {
    setRerunBusy(true);
    try {
      const aliases = await request<StaffImportAliasList>(
        { command: "listAliases" },
        { importKind: "CATALOG_ITEMS" },
      );
      const headers = columns.map((column) => column.header);
      setDecisions(
        (current) =>
          rerunSuggestions({
            headers,
            learnedAliases: aliases.learned,
            current,
          }) as readonly ImportColumnDecision[],
      );
      setMessage({
        tone: "success",
        text: "Suggestions were run again for the columns that are not mapped.",
      });
    } catch (error) {
      setMessage({ tone: "error", text: describeImportError(error).message });
    } finally {
      setRerunBusy(false);
    }
  }

  function goToColumn(columnIndex: number) {
    // The column gets the focus, not the heading of the step.
    keepFocus.current = true;
    nonce.current += 1;
    setFocusRequest({ index: columnIndex, nonce: nonce.current });
    setStep("map");
  }

  // --- step 5: save, submit, cancel -------------------------------------------------------------

  const blockers = submitBlockers({
    hasJob: detail !== null,
    dirty,
    decisions,
    defaultItemType,
    columns,
  });

  async function save() {
    setMessage(null);
    setServerIssues([]);
    const body = {
      decisions: toDecisionInputs(decisions),
      defaultItemType,
    };
    if (detail === null) {
      if (source === null) return;
      const outcome = await commands.run("createDraft", undefined, {
        objectPath: source.objectPath,
        fileName: source.fileName,
        expectedSha256: source.preview.sha256,
        sheetIndex: source.preview.sheetIndex,
        headerRowNumber: source.preview.headerRowNumber,
        ...body,
      });
      if (!outcome.ok) {
        if (outcome.failure.kind === "mapping") setServerIssues(outcome.failure.mappingIssues);
        setMessage(describeOutcome(outcome, ""));
        return;
      }
      // The job exists now. Its page opens at the save step with the answer already in the cache.
      withoutPrompt(() =>
        navigate(`/imports/${encodeURIComponent(outcome.detail.job.id)}`, {
          replace: true,
          state: { step: "save", notice: "saved" },
        }),
      );
      return;
    }
    if (mapping === null) return;
    const outcome = await commands.run("saveMapping", detail.job.id, {
      expectedRevision: mapping.revision,
      ...body,
    });
    if (outcome.ok) {
      setMessage({ tone: "success", text: "The draft was saved." });
      return;
    }
    if (outcome.failure.kind === "mapping") setServerIssues(outcome.failure.mappingIssues);
    setMessage(describeOutcome(outcome, ""));
  }

  async function submit() {
    if (blockers.length > 0) {
      setMessage(null);
      setAttempted(true);
      const first = blockers.find((blocker) => blocker.columnIndex !== null);
      if (first?.columnIndex != null) goToColumn(first.columnIndex);
      else blockersRef.current?.focus();
      return;
    }
    if (detail === null || mapping === null) return;
    const problems = settingsIssues(settings);
    if (problems.defaultTimeZone !== undefined || problems.sourceSystem !== undefined) {
      setMessage(null);
      setSettingsAttempted(true);
      const target =
        problems.defaultTimeZone !== undefined
          ? "import-setting-time-zone"
          : "import-setting-source-system";
      document.getElementById(target)?.focus();
      return;
    }
    setMessage(null);
    setServerIssues([]);
    const validation = toSubmitSettings(settings);
    const outcome = await commands.run("submitMapping", detail.job.id, {
      expectedVersion: detail.job.version,
      expectedRevision: mapping.revision,
      ...(validation === undefined ? {} : { validation }),
    });
    if (outcome.ok) {
      setAttempted(false);
      setMessage({
        tone: "success",
        text: "The draft was submitted. It is waiting for validation. Nothing has reached the catalog.",
      });
      return;
    }
    if (outcome.failure.kind === "mapping") setServerIssues(outcome.failure.mappingIssues);
    setMessage(describeOutcome(outcome, ""));
  }

  async function cancelDraft(reason: string): Promise<string | null> {
    if (detail === null) return "This draft was not found.";
    const outcome = await commands.run("cancelJob", detail.job.id, {
      expectedVersion: detail.job.version,
      reason,
    });
    if (outcome.ok) {
      setMessage({ tone: "success", text: "The draft was cancelled." });
      return null;
    }
    return describeOutcome(outcome, "").text;
  }

  function startOver() {
    chooseAnotherFile();
    setStep("upload");
  }

  const itemTypeText = isItemTypeMapped(decisions)
    ? "Taken from the Item type column"
    : defaultItemType === null
      ? "Not set"
      : itemTypeLabels[defaultItemType];

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title={detail === null ? "New import" : "Import draft"}
        description="Map a spreadsheet to the catalog fields. Nothing here changes the catalog."
        breadcrumbs={<BackLink to="/imports">Back to imports</BackLink>}
      />

      {detail === null ? null : <DraftFacts detail={detail} />}

      <Stepper current={step} context={context} onSelect={setStep} />

      <div className="min-w-0">
        {step === "upload" ? (
          <UploadStep
            state={upload.state}
            preview={preview}
            savedDraft={detail !== null}
            onChoose={upload.start}
            onRetry={upload.retry}
            onChooseAnother={chooseAnotherFile}
            onContinue={() => setStep(nextStep("upload", context) ?? "header")}
          />
        ) : null}
        {step === "header" ? (
          <HeaderStep
            preview={detail === null ? preview : null}
            savedMapping={mapping}
            busy={headerBusy}
            error={headerError}
            onChange={(change) => void changeHeader(change)}
            onContinue={() => setStep("map")}
          />
        ) : null}
        {step === "map" ? (
          <MapStep
            columns={columns}
            decisions={decisions}
            defaultItemType={defaultItemType}
            readOnly={readOnly}
            attempted={attempted}
            serverIssues={serverIssues}
            focusRequest={focusRequest}
            rerunBusy={rerunBusy}
            onDecisions={(next) => {
              setDecisions(next);
              setMessage(null);
            }}
            onDefaultItemType={(next) => {
              setDefaultItemType(next);
              setMessage(null);
            }}
            onRerun={() => void rerun()}
            onContinue={() => setStep("review")}
          />
        ) : null}
        {step === "review" ? (
          <ReviewStep
            columns={columns}
            decisions={decisions}
            defaultItemType={defaultItemType}
            rows={reviewRows}
            useSaved={readOnly}
            fromPreview={mapping === null}
            loading={mapping !== null && rowsQuery.isPending}
            errorMessage={
              mapping !== null && rowsQuery.isError
                ? describeImportError(rowsQuery.error).message
                : null
            }
            hasMore={mapping !== null && rowsQuery.hasNextPage}
            loadingMore={rowsQuery.isFetchingNextPage}
            onLoadMore={() => void rowsQuery.fetchNextPage()}
            onRetry={() => void rowsQuery.refetch()}
            onContinue={() => setStep("save")}
          />
        ) : null}
        {step === "save" ? (
          <SaveStep
            ref={blockersRef}
            detail={detail}
            countsText={describeCounts(decisions)}
            itemTypeText={itemTypeText}
            dirty={dirty}
            readOnly={readOnly}
            blockers={blockers}
            attempted={attempted}
            message={message}
            pending={commands.pending}
            onSave={() => void save()}
            onSubmit={() => void submit()}
            onGoToColumn={goToColumn}
            onCancelDraft={() => setCancelling(true)}
            onStartOver={startOver}
            settings={settings}
            settingsIssues={settingsAttempted ? settingsIssues(settings) : {}}
            onSettings={setSettings}
          />
        ) : null}
      </div>

      {step !== "save" ? <StatusMessage message={message} /> : null}

      {cancelling ? (
        <CancelDraftDialog onConfirm={cancelDraft} onClose={() => setCancelling(false)} />
      ) : null}
    </div>
  );
}
